import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, Input, Label, Modal } from "../../../Components/Common/Tailwind";
import { useNavigate, useLocation } from "react-router-dom";
import BgFrame from "../../../Components/Common/BgFrame";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import { SUBDOMAIN } from "../../../assets/meta/meta";
import { API, BACKEND_HOST } from "../../../api";
import DaumPostcode from "react-daum-postcode";
import { calcCouponSaleAmount } from "../../../common/utils/coupon_util";
import CouponShoppingSelectModal from "../../../Components/modal/CouponShoppingSelectModal";
import { getAccessToken } from "../../../api/auth";
import { useAuthContext } from "../../../Components/AuthContext";
import { usePGModules, useTossPaymentWidget, useOrderAgreements, dispatchPayment } from "../../../common/payments";
import { CollapsibleSection, RefundPolicySection, PGSelectorSection, TotalPriceDisplay, PayButton } from "../components";

const ShoppingOrderProcess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAuthContext();

  // --- 공통 훅 ---
  const { pgModules, selectedPG, setSelectedPG } = usePGModules();
  const {
    cancelRuleAgree, purchaseAllAgree, purchaseOneAgree,
    setCancelRuleAgree, togglePurchaseAgreeOptions, allAgreed,
  } = useOrderAgreements();

  const [paying, setPaying] = useState(false);

  // --- 배송지 ---
  const [addressPopupOpen, setAddressPopupOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [addressDetail, setAddressDetail] = useState("");
  const [memo, setMemo] = useState("");

  // --- 쿠폰 ---
  const [selectCoupon, setSelectCoupon] = useState([]);
  const [couponModal, setCouponModal] = useState(false);
  const [targetItem, setTargetItem] = useState({});

  const toggleShoppingDiscountCouponSelection = (product_id, coupon_id, option_id, discount_amount, discount_type, min_purchase, discount_rate, max_discount = 0) => {
    setSelectCoupon((prev) => {
      let _prev = [...prev];
      const item = { coupon_id, product_id, option_id, discount_amount, discount_type, min_purchase, discount_rate, max_discount };
      const selected = _prev.find((i) => i.product_id === product_id && i.coupon_id === coupon_id && i.option_id === option_id);
      const blocked = _prev.find((i) => i.product_id === product_id && i.coupon_id === coupon_id && i.option_id !== option_id);
      if (blocked) return _prev;
      _prev = _prev.filter((i) => !(i.product_id === product_id && i.option_id !== option_id));
      return selected ? _prev : [..._prev, item];
    });
  };

  // --- 가격 ---
  const totalOrderPrice = useMemo(() => {
    const subtotal = location.state.products
      .map((item) => item.price * item.quantity)
      .reduce((prev, curr) => prev + curr, 0);
    if (location.state.products.free_delivery_condition > 0 && subtotal < location.state.products.free_delivery_condition) {
      return subtotal + location.state.products.delivery_fee;
    }
    return subtotal;
  }, [location.state.products]);

  const totalOrderPriceWithVariation = useMemo(() => {
    let _total = location.state.products
      .flatMap((item) => {
        if (item.selectVariation)
          return item.selectVariation.map((variation) => {
            const _coupon = selectCoupon.find((c) => c.product_id === item.id && c.option_id === variation.id);
            const basePrice = variation.is_required ? (item.price + variation.price) * variation.quantity : variation.price * variation.quantity;
            return _coupon ? basePrice - calcCouponSaleAmount(_coupon, basePrice) : basePrice;
          });
        return item.price * item.quantity;
      })
      .reduce((prev, curr) => prev + curr, 0);

    location.state.products.forEach((product) => {
      if (product.free_delivery_condition > 0 && _total < product.free_delivery_condition) {
        _total += product.delivery_fee;
      }
    });
    return _total;
  }, [location.state.products, selectCoupon]);

  const effectivePrice = location.state.products.find((item) => item.selectVariation)
    ? totalOrderPriceWithVariation
    : totalOrderPrice;

  // --- Toss 위젯 ---
  const { paymentWidgetRef } = useTossPaymentWidget({
    userInfo,
    totalOrderPrice: effectivePrice,
    isTossSelected: selectedPG?.code === "toss",
  });

  // --- 결제 공통 ---
  const doPaymentDispatch = async (orderId, amount) => {
    await dispatchPayment({
      selectedPG,
      paymentWidgetRef,
      orderId,
      orderName: "쇼핑 종합",
      amount,
      userInfo,
      urls: {
        successUrl: `${BACKEND_HOST}/shop/order/`,
        failUrl: `https://${SUBDOMAIN}.${window.AMZ_ENV.VITE_DOMAIN}/shop`,
        niceReturnUrl: "/shop/order-nice/",
        hectoNotiUrl: `${BACKEND_HOST}/shop/order-hecto/`,
        hectoNextUrl: `${BACKEND_HOST}/package/order-done-hecto/`,
        hectoCancUrl: `${BACKEND_HOST}/package/order-fail-hecto/`,
      },
      failNavigate: () => navigate(`/shop`),
    });
  };

  const variationProcessPayment = async () => {
    if (!userInfo) { toast.error("로그인이 필요한 서비스입니다."); return; }
    const req_data = {
      cart_data: location.state.products.flatMap((item) =>
        item.selectVariation.map((variation) => ({
          variation: variation.id,
          shopping_product: item.id,
          quantity: variation.quantity,
          coupon_id: selectCoupon.find((c) => c.product_id === item.id && c.option_id === variation.id)?.coupon_id || "",
        }))
      ),
      address: address + " " + addressDetail,
      recipient: userInfo.username,
      phone_num: userInfo.phone_num,
      memo,
    };

    setPaying(true);
    try {
      const accessToken = getAccessToken();
      if (!accessToken) return;
      const response = await API.post(`/shop/preorder/`, req_data, {
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      });
      const data = response.data;
      if ("error" in data) { toast.error(data.error); navigate(`/shop`); return; }
      await doPaymentDispatch(data.orderId, totalOrderPriceWithVariation);
    } catch (error) {
      console.log(error);
      if (error.response) {
        toast.error(`결제에 실패하였습니다. ${error.response.data?.error || String(error.response.data)}`);
      } else {
        toast.error("지금은 구매가능 시간이 아니거나 품절된 상품입니다.");
      }
      navigate(`/shop`);
    } finally {
      setPaying(false);
    }
  };

  const processPayment = async () => {
    const req_data = {
      cart_data: location.state.products.map((item) => ({
        ...item, shopping_product: item.id, quantity: item.quantity,
      })),
      address: address + " " + addressDetail,
      recipient: userInfo.name,
      phone_num: userInfo.phone_num,
      memo,
    };

    const accessToken = getAccessToken();
    if (!accessToken) { toast.error("로그인이 필요한 서비스입니다."); navigate(`/login`); return; }

    setPaying(true);
    try {
      const response = await API.post(`/shop/preorder/`, req_data, {
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      });
      const data = response.data;
      if ("error" in data) { toast.error(data.error); navigate(`/shop`); return; }
      await doPaymentDispatch(data.orderId, totalOrderPrice);
    } catch (error) {
      console.log(error);
      if (error.response) {
        toast.error(`결제에 실패하였습니다. ${error.response.data?.error || String(error.response.data)}`);
      } else {
        toast.error("지금은 구매가능 시간이 아니거나 품절된 상품입니다.");
      }
      navigate(`/shop`);
    } finally {
      setPaying(false);
    }
  };

  return (
    <MobileViewContainer>
      <BgFrame>
        <div className="space-y-2.5">
          {/* 주문자 + 배송지 */}
          <CollapsibleSection title="주문자" defaultOpen>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label htmlFor="billinginfo-firstName" className="form-label">구매자</Label>
                  <Input value={userInfo.name} type="text" id="billinginfo-firstName" placeholder="구매자명" disabled={!userInfo.phone_num} />
                </div>
                <div className="flex-1">
                  <Label htmlFor="billinginfo-phone" className="form-label">휴대폰번호</Label>
                  <Input value={userInfo.phone_num ?? ""} type="text" id="billinginfo-phone" placeholder="휴대폰번호" disabled />
                </div>
              </div>

              <Modal toggle={() => setAddressPopupOpen((prev) => !prev)} isOpen={addressPopupOpen} modalClassName="fadeInUp" centered>
                <div className="modal-header">
                  <h5 className="modal-title mt-0">주소검색</h5>
                  <button type="button" onClick={() => setAddressPopupOpen(false)} className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                </div>
                <div className="modal-body">
                  <DaumPostcode onComplete={(data) => { setAddress(data.address); setAddressPopupOpen(false); }} />
                </div>
              </Modal>

              <div>
                <Label htmlFor="billinginfo-address" className="form-label">배송지 주소</Label>
                <div className="flex gap-2">
                  <Input value={address} type="text" className="form-control flex-1" id="billinginfo-address" placeholder="배송지 주소" disabled />
                  <Button
                    type="button"
                    size="sm"
                    className="font-gmarket text-white shrink-0"
                    style={{ backgroundColor: "#f6475f", borderColor: "#f6475f" }}
                    onClick={() => setAddressPopupOpen(true)}
                  >
                    <i className="ri-search-line label-icon align-middle" style={{ color: "#fff" }} /> 주소검색
                  </Button>
                </div>
                <Input value={addressDetail} onChange={(e) => setAddressDetail(e.target.value)} type="text" className="form-control mt-2" placeholder="상세주소" />
                <Input value={memo} onChange={(e) => setMemo(e.target.value)} type="text" className="form-control mt-2" placeholder="배송 메모" />
                <div className="text-sm text-gray-500 mt-2">예) 지점 방문수령으로 oo월 oo일 수령을 원합니다.</div>
              </div>
            </div>
          </CollapsibleSection>

          {/* 주문상품 */}
          <CollapsibleSection title="주문상품" defaultOpen>
            {location.state.products.map((product) => {
              if (product.selectVariation) {
                return (
                  <div key={product.id} className="mb-4">
                    <span className="fw-semibold font-gmarket">{product.name}</span>
                    {product.selectVariation.map((item) => {
                      if (!item.quantity) return null;
                      if (item.is_required) {
                        return (
                          <div key={item.id} 
                          className="flex justify-between"
                          style={{ borderBottom: "1px solid #ededed", padding: "10px 0", marginTop: "10px" }}>
                            <div className="flex justify-between items-center" style={{ padding: "10px 0" }}>
                              <span>{item.value} ({item.price >= 0 ? "+" + item.price.toLocaleString() : item.price.toLocaleString()}원) X {item.quantity}개</span>
                            </div>
                            <Button
                            variant="outline"
                            size="sm"
                              onClick={() => {
                                setCouponModal(true);
                                setTargetItem({
                                  product_id: product.id,
                                  price: product.price + item.price,
                                  quantity: item.quantity,
                                  category: product.category,
                                  option_id: item.id,
                                });
                              }}
                            >
                              쿠폰선택하기
                            </Button>
                            {selectCoupon
                              .filter((c) => c.product_id === product.id && c.option_id === item.id)
                              .map((couponSelected) => (
                                <div key={couponSelected.coupon_id} style={{ padding: "10px 0" }}>
                                  <span>쿠폰 적용 </span>
                                  <span className="d-block text-right font-semibold">
                                    {calcCouponSaleAmount(couponSelected, (product.price + item.price) * item.quantity).toLocaleString()}원 할인
                                  </span>
                                </div>
                              ))}
                            <div className="flex justify-end mb-5">
                              <span className="font-gmarket">{((product.price + item.price) * item.quantity).toLocaleString()}원</span>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={item.id} className="flex justify-between" style={{ borderBottom: "1px solid #ededed", padding: "10px 0", marginTop: "10px" }}>
                          <span>
                            {item.value} ({item.price >= 0 ? "+" + item.price.toLocaleString() : item.price.toLocaleString()}원) X {item.quantity}개
                            <span className="font-gmarket text-brand">&nbsp;추가상품</span>
                          </span>
                          <span className="font-gmarket">{(item.price * item.quantity).toLocaleString()}원</span>
                        </div>
                      );
                    })}

                    <p className="text-right mt-5">
                      <span className="font-gmarket">
                        배송비&nbsp;
                        {product.free_delivery_condition > 0
                          ? (product.free_delivery_condition >
                            product.selectVariation
                              .map((v) => v.is_required ? (product.price + v.price) * v.quantity : v.price * v.quantity)
                              .reduce((a, b) => a + b, 0)
                            ? product.delivery_fee.toLocaleString()
                            : 0)
                          : product.delivery_fee.toLocaleString()}원
                      </span>
                    </p>
                    <TotalPriceDisplay amount={totalOrderPriceWithVariation} label="결제금액" />
                  </div>
                );
              }

              if (!product.quantity) return null;
              return (
                <div key={product.id} className="p-5 border border-gray-700 rounded mb-4">
                  <span className="fw-semibold font-gmarket">{product.name}</span>
                  <div className="text-right mt-2" style={{ borderBottom: "1px solid #ededed", padding: "10px 0" }}>
                    {new Intl.NumberFormat().format(product.price)}원 X {product.quantity}개
                  </div>
                  <TotalPriceDisplay amount={product.price * product.quantity} label="결제금액" />
                </div>
              );
            })}
          </CollapsibleSection>

          {/* 환불 규정 */}
          <RefundPolicySection
            type="shopping"
            cancelRuleAgree={cancelRuleAgree}
            purchaseOneAgree={purchaseOneAgree}
            purchaseAllAgree={purchaseAllAgree}
            onCancelRuleChange={setCancelRuleAgree}
            onPurchaseAgreeChange={togglePurchaseAgreeOptions}
            onAllAgreeChange={togglePurchaseAgreeOptions}
          />

          {/* PG 선택 */}
          <PGSelectorSection pgModules={pgModules} selectedPG={selectedPG} onSelect={setSelectedPG} />

          {/* 하단 고정 결제 버튼 여백 */}
          <div style={{ height: "100px" }} />
        </div>

        {/* 결제 버튼 */}
        <PayButton
          loading={paying}
          onClick={async () => {
            if (!allAgreed) { toast.warning("동의항목을 확인해주세요."); return; }
            if (!(userInfo.name && userInfo.phone_num) || !address) {
              toast.warning("구매자명 및 주소를 입력해주세요.");
              return;
            }
            if (location.state.products.find((item) => item.selectVariation)) {
              await variationProcessPayment();
            } else {
              await processPayment();
            }
          }}
        />

        <CouponShoppingSelectModal
          setVisible={setCouponModal}
          visible={couponModal}
          targetItem={targetItem}
          selectCoupon={selectCoupon}
          setSelectCoupon={setSelectCoupon}
          toggleShoppingDiscountCouponSelection={toggleShoppingDiscountCouponSelection}
          navigate={navigate}
        />
      </BgFrame>
    </MobileViewContainer>
  );
};

export default ShoppingOrderProcess;
